import React, {useContext, useEffect, useRef} from 'react';

// Reducer
import {AppContext} from '../contexts/context';

// containers
import {recordId} from './RecordData'
import {idbGetData} from "./indexedDB"

// Components
import {convertArraysToObjects, removeQueryParams} from '../components/AppUtils';

// Firebase
import {storage} from "./Firebase"
import {getDownloadURL, ref} from "firebase/storage";

// Tools
import LZString from 'lz-string';
import {getAppUrl} from "./AppUrl";
import {Capacitor} from "@capacitor/core";
import {App} from "@capacitor/app";
import * as fflate from "fflate";

function isSafari() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') &&
    !ua.includes('chrome') &&
    !ua.includes('chromium') &&
    !ua.includes('android');
};

const DataImport = () => {
  // global context
  const [state, dispatch] = useContext(AppContext);
  const [activeSearchParams, setActiveSearchParams] = React.useState(null);
  const [importSchemaStatus, setImportSchemaStatus] = React.useState('pending');
  // Firebase storage reference
  const storageRef = ref(storage);


  async function fetchSchemaData(url) {
    console.debug("DataImport | fetchSchemaData: started ... ");
    try {
      const response = await fetch(url);
      console.debug("DataImport | fetchSchemaData: await fetch ... ");
      try {
        const result = await response.json();
        console.debug("DataImport | fetchSchemaData: Schema downloaded ... ");
        console.debug("DataImport | fetchSchemaData: Schema:", result);
        console.debug("DataImport | fetchSchemaData: Schema as JSON:", JSON.stringify(result));
        // const nfcSchemaCompressed = fflate.compressSync(result, { level: 6, mem: 8 });
        dispatch({type: 'data/S/updated', value: result});
        // dispatch({type: 'data/S/updatedCompressed', value: nfcSchemaCompressed});
        setImportSchemaStatus('done');
      } catch (error) {
        console.error('DataImport | fetchSchemaData: response failed:', error);
        setImportSchemaStatus('failed');
      }
    } catch (error) {
      console.error('DataImport | fetchSchemaData: fetch failed:', error);
      setImportSchemaStatus('failed');
    }
  }

  function importFromIdb(obj, key) {
    var result = false;
    idbGetData(obj[key]).then(data => {
      if (data) {
        console.log(`DataImport | IndexedDB: Schema with ID ${obj[key]} from IndexedDB:`, data);
        dispatch({type: 'data/S/updated', value: data});
        setImportSchemaStatus('done')
        result = true;
      } else {
        console.error("DataImport | IndexedDB: load error:", obj[key])
      }
    }).catch((res) => {
      console.debug("DataImport | IndexedDB: idb error", res)
    });
    return result;
  }

  function processDataImport(obj) {
    const requiredSchema = ["S"];
    const requiredParameters = ["P", "PP", "PR"];

    console.debug(`DataImport | processDataImport for`, obj);

    // Error handling
    if (!requiredSchema.some(key => obj.hasOwnProperty(key))) {
      console.error(`DataImport | processDataImport failed, schema missing!`, obj);
      return false;
    }

    if (!requiredParameters.some(key => obj.hasOwnProperty(key))) {
      console.error(`DataImport | processDataImport failed, some parameters missing!`, obj);
      return false;
    }

    if (Object.keys(obj).length < 2) {
      console.error(`DataImport | processDataImport failed, not enough data included!`, obj);
      return false;
    }

    // Process Import
    for (const key in obj) {
      console.debug(`DataImport | processDataImport for key`, key);
      if (obj.hasOwnProperty(key)) {
        if (key in recordId) {
          if (key === 'S') {
            setImportSchemaStatus('pending')

            var idbResult = importFromIdb(obj, key);
            console.debug(`DataImport | idbResult:`, idbResult);

            if (idbResult === false && navigator.onLine) {
              console.debug(`DataImport | processDataImport: Download started for ${obj[key]} ... `);
              try {
                getDownloadURL(ref(storageRef, `${obj[key]}/schema.json`))
                  .then((url) => {
                    console.debug("DataImport | processDataImport: URL", url)
                    try {
                      fetchSchemaData(url);
                    } catch (error) {
                      console.error("DataImport | processDataImport: error", error);
                      if (importSchemaStatus !== 'done') {
                        setImportSchemaStatus('failed');
                      }
                    }
                  })
                  .catch((data) => {
                    dispatch({
                      type: 'app/ui/snackbar/show',
                      message: `Failed to retrieve data ${obj[key]}`,
                      severity: 'warning'
                    });
                    console.error("DataImport | processDataImport: Download data: ", data);
                    if (importSchemaStatus !== 'done') {
                      setImportSchemaStatus('failed');
                    }
                  });
              } catch (error) {
                console.error('DataImport | processDataImport: Error getDownloadURL', error);
                if (importSchemaStatus !== 'done') {
                  setImportSchemaStatus('failed');
                }
              }
            } else {
              if (importSchemaStatus !== 'done') {
                setImportSchemaStatus('offline');
              }
              return false;
            }
          } else {
            let subObj = {[key]: obj[key]};
            console.debug(`DataImport | processDataImport: subObj`, subObj);

            dispatch({type: `data/${key}/imported`, value: subObj});
            dispatch({type: `ndef/control/updateScanTime`});
            if (!(Object.keys(obj[key]).length === 0 && obj[key].constructor === Object)) {
              console.debug(`DataImport | processDataImport: Dispatch data/${key}/updated with value`, obj[key]);
            } else {
              console.warn(`DataImport | processDataImport: ${key} is empty!`);
            }
          }
        } else {
          console.warn(`DataImport | processDataImport: failed, unknown key not processed!`, obj);
        }
      }
    }
    return true;
  }

  const importDataByKeys = (searchParams) => {
    let result = true;

    console.debug('DataImport | importDataByKeys: Try to restore data from URL:', searchParams)
    var compressedDataString = searchParams.get('D')
    if (compressedDataString === null) {
      console.error('Error: compressedDataString:', compressedDataString)
      return false;
    }
    console.debug('DataImport | importDataByKeys: compressedDataString', compressedDataString)
    var dataImportString = LZString.decompressFromEncodedURIComponent(compressedDataString);

    if (dataImportString === null || (typeof dataImportString === "string" && dataImportString.trim() === "")) {
      console.error('DataImport | importDataByKeys: decompress failed', dataImportString)
      return false;
    }

    console.debug('DataImport | importDataByKeys: dataString', dataImportString)
    var dataImportObject = JSON.parse(dataImportString);
    if (dataImportObject !== null) {
      console.debug("DataImport | importDataByKeys: dataImportObject", dataImportObject);
      var dataimportRestoredObject = convertArraysToObjects(dataImportObject)

      if (dataimportRestoredObject !== null) {
        console.debug("DataImport | importDataByKeys: dataimportRestoredObject", dataimportRestoredObject);
        result = processDataImport(dataimportRestoredObject);
      } else {
        console.error('DataImport | importDataByKeys: Convert Array failed!')
        result = false;
      }
    } else {
      console.error('DataImport | importDataByKeys: Parsing data failed!')
      result = false;
    }

    return result;
  }

  // Support Universal links on iOS
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return;
    App.addListener('appUrlOpen', (event) => {
      console.log("AppUrlListener | event.url: ", event.url, " |")
      const universalLinkUrl = new URL(event.url);
      setActiveSearchParams(universalLinkUrl.searchParams);
    });
  }, []);

  // Initially check uri for import data
  useEffect(() => {
      // Has already query data to import?
      const myURL = getAppUrl();
    setActiveSearchParams(myURL.searchParams);
  }, []);

  useEffect(() => {
    // Has query data to import?
    if (!activeSearchParams) return;

    if (!activeSearchParams.has('D')) return;
    if (activeSearchParams.has('N')) {
      dispatch({
        type: 'app/ui/importConfigName/set',
        value: activeSearchParams.get('N')
      });
    }
    console.debug(`DataImport | UseEffect`);

    // Only import data on devices with nfc available
    if (isSafari() && Capacitor.getPlatform() !== 'ios') {
      console.log("DataImport | UseEffect: NFC not available, aborting import!")
      // TODO Show info to user
      return;
    }

    // Import initializion error?
    if (!importDataByKeys(activeSearchParams)) {
      dispatch({
        type: 'app/ui/dialog/error',
        title: `Error importing data`,
        text: `An error has occurred while processing the data. Try again or ask for a new link.`
      });
    } else {
      dispatch({type: 'app/ui/dialog/default/show', value: 'import'});
    }

    setActiveSearchParams(null);
    removeQueryParams();
  }, [activeSearchParams]);

  useEffect(() => {
    console.debug("DataImport | switch:", importSchemaStatus)
    switch (importSchemaStatus) {
      case 'failed':
        dispatch({type: 'app/ui/dialog/default/close'});
        dispatch({
          type: 'app/ui/dialog/error',
          title: `Error importing data`,
          text: `Schema download failed, file could not be found.`
        });
        break;
      case 'offline':
        dispatch({type: 'app/ui/dialog/default/close'});
        dispatch({
          type: 'app/ui/dialog/error',
          title: `Error importing data`,
          text: `The data is out of date and needs to be updated. Establish an online connection and try again.`
        });
        break;
      case 'done':
        dispatch({type: 'app/ui/dialog/default/show', value: 'import'});
        break;
      case 'pending':
      default:
        break;
    }
  }, [importSchemaStatus]);

  return (
    <div>
    </div>
  );

}

export default DataImport;
